import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaBuilding, FaPencilAlt, FaTrashAlt, FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { IoMdAddCircle } from 'react-icons/io';
import { Row, Col, Button, Form, Dropdown, Modal, Alert, Tooltip, Overlay } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './departments.css';

export default function DepartmentTable() {
  const [departments, setDepartments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedDepartment, setExpandedDepartment] = useState(null);
  const [departmentTypes, setDepartmentTypes] = useState({});

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(
          `https://backend.ems.hiradentallab.com/api/departments?search=${searchQuery}`
        );
        // Sort departments alphabetically by name
        const sortedDepartments = response.data.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setDepartments(sortedDepartments);

        // Initialize department types
        const typesMap = {};
        sortedDepartments.forEach(dept => {
          typesMap[dept._id] = dept.types || [];
        });
        setDepartmentTypes(typesMap);
      } catch (error) {
        console.error('Error fetching departments:', error);
        setShowErrorAlert(true);
        setErrorMessage('Error fetching departments. Please try again.');
        hideAlertAfterDelay(setShowErrorAlert);
      }
    };

    fetchDepartments();

    document.body.style.overflowY = 'auto';
    return () => {
      document.body.style.overflowY = '';
    };
  }, [searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(e.target.value);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDelete = (id) => {
    setDepartmentToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`https://backend.ems.hiradentallab.com/api/departments/delete/${departmentToDelete}`);
      setShowSuccessAlert(true);
      setDepartments(departments.filter(department => department._id !== departmentToDelete));
      hideAlertAfterDelay(setShowSuccessAlert);
    } catch (error) {
      console.error('Error deleting department:', error);
      setShowErrorAlert(true);
      setErrorMessage('Failed to delete department. Please try again.');
      hideAlertAfterDelay(setShowErrorAlert);
    } finally {
      setShowDeleteModal(false);
    }
  };

  const hideAlertAfterDelay = (setAlertFunction) => {
    setTimeout(() => {
      setAlertFunction(false);
    }, 3000);
  };

  const toggleDepartmentTypes = (departmentId) => {
    if (expandedDepartment === departmentId) {
      setExpandedDepartment(null);
    } else {
      setExpandedDepartment(departmentId);
    }
  };

  const pageRange = () => {
    const totalPages = Math.ceil(departments.length / rowsPerPage);
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = departments.slice(indexOfFirstRow, indexOfLastRow);
  const totalEntries = departments.length;

  return (
    <div className='container-fluid'>
      <h1 className="department-title" style={{color:"#0353a4"}}>
        <FaInfoCircle className="salary-icon" /> Departments Information
      </h1>
      <hr />
      <h2 className="department-subtitle">
        <FaBuilding className="salary-icon" /> All Departments
      </h2>
      <hr />

      {/* Success and Error Alerts */}
      {showSuccessAlert && (
        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible>
          Department deleted successfully!
        </Alert>
      )}
      {showErrorAlert && (
        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible>
          {errorMessage}
        </Alert>
      )}

      <div className="table-container">
        <Link to="/add-departments" style={{ textDecoration: 'none' }}>
          <Button variant="primary" style={{ marginBottom: '1%', marginLeft: '7px' }}>
            <IoMdAddCircle /> Add Department
          </Button>
        </Link>

        <div className="filter-container">
          <Col xs={12} md={6} className="filter-left">
            <Dropdown className="d-inline mx-2" autoClose="inside">
              <Dropdown.Toggle id="dropdown-autoclose-inside">
                Show Entries
              </Dropdown.Toggle>
              <Dropdown.Menu className="custom-dropdown-menu">
                <Dropdown.Item href="#">15</Dropdown.Item>
                <Dropdown.Item href="#">25</Dropdown.Item>
                <Dropdown.Item href="#">50</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
          <Col xs={12} md={6} className="filter-right">
            <Form className="d-flex">
              <Form.Control
                type="search"
                placeholder="Search"
                aria-label="Search"
                className="depts-search-bar"
                value={searchQuery}
                onChange={handleSearch}
              />
            </Form>
          </Col>
        </div>

        <div>
          <section>
            <div id="main-box">
              <div className="depts-table-responsive">
                <table className="table table-striped table-style table-sm">
                  <thead>
                    <tr role="row">
                      <th style={{ backgroundColor: '#007bff', color: '#ffffff' }}>SR</th>
                      <th style={{ backgroundColor: '#007bff', color: '#ffffff' }}>Department</th>
                      <th style={{ textAlign: 'center', backgroundColor: '#007bff', color: '#ffffff' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="department-table">
                    {currentRows.map((department, index) => (
                      <React.Fragment key={department._id}>
                        <tr role="row" className={index % 2 === 0 ? 'even' : 'odd'}>
                          <td>{index + 1 + indexOfFirstRow}</td>
                          <td>
                            <div className="department-name-container">
                              <span>{department.name}</span>
                              {departmentTypes[department._id] && departmentTypes[department._id].length > 0 && (
                                <button
                                  className="btn-types-toggle"
                                  onClick={() => toggleDepartmentTypes(department._id)}
                                  aria-label="Show department types"
                                >
                                  {expandedDepartment === department._id ? (
                                    <FaChevronUp className="chevron-icon" />
                                  ) : (
                                    <FaChevronDown className="chevron-icon" />
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <Link to={`/edit-departments/${department._id}`}>
                              <Button size="sm" className="btn btn-warning my-1">
                                <FaPencilAlt />
                              </Button>
                            </Link>
                            <Button
                              type="button"
                              name="delete"
                              size="sm"
                              className="btn btn-danger my-1"
                              onClick={() => handleDelete(department._id)}
                            >
                              <FaTrashAlt />
                            </Button>
                          </td>
                        </tr>
                        {expandedDepartment === department._id && (
                          <tr className="types-row">
                            <td colSpan="3">
                              <div className="types-container">
                                <h6 className="types-title">Department Types:</h6>
                                {departmentTypes[department._id] && departmentTypes[department._id].length > 0 ? (
                                  <ul className="types-list">
                                    {departmentTypes[department._id].map((type, idx) => (
                                      <li key={idx} className="type-item">
                                        {type}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="no-types">No types defined for this department</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot className="table-footer">
                    <tr className="departments-table-footer">
                      <td>Total</td>
                      <td>-</td>
                      <td>{totalEntries}</td>
                    </tr>
                  </tfoot>
                </table>

                <Row className="mt-2">
                  <Col xs={12} md={6} className="d-flex align-items-center">
                    <p className="showing-entries">
                      Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, totalEntries)} of {totalEntries} entries
                    </p>
                  </Col>
                  <Col xs={12} md={6} className="d-flex justify-content-end">
                    <nav>
                      <ul className="pagination">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <a
                            href="/#"
                            className="page-link"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(currentPage - 1);
                            }}
                          >
                            Previous
                          </a>
                        </li>
                        {pageRange().map((page) => (
                          <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <a
                              href="/#"
                              className="page-link"
                              onClick={(e) => {
                                e.preventDefault();
                                handlePageChange(page);
                              }}
                            >
                              {page}
                            </a>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === Math.ceil(totalEntries / rowsPerPage) ? 'disabled' : ''}`}>
                          <a
                            href="/#"
                            className="page-link"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(currentPage + 1);
                            }}
                          >
                            Next
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </Col>
                </Row>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this department?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}